import { createContext, useContext, useState, useEffect } from "react";
import api, { clearAuthStorage, SESSION_EXPIRED_EVENT } from "./api";
import { syncBuyerProfileToDraft } from "@/lib/buyerProfile";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("scaffoindia_token");
    const savedUser = localStorage.getItem("scaffoindia_user");
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch { /* ignore */ }
    }
    // Validate with server when possible; keep cached session if validation fails.
    (async () => {
      try {
        if (token) await refreshUser();
      } catch {
        /* expired or invalid token — stay on page until manual logout */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // The API client fires this when a 401 could not be recovered by refreshing the token.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { token, user: userData, refresh_token: refreshToken } = res.data;
    localStorage.setItem("scaffoindia_token", token);
    localStorage.setItem("scaffoindia_user", JSON.stringify(userData));
    if (refreshToken) localStorage.setItem("scaffoindia_refresh_token", refreshToken);
    setUser(userData);
    if (userData?.role === "buyer") syncBuyerProfileToDraft(userData);
    return userData;
  };

  const loginWithMagicToken = async (magicToken) => {
    const res = await api.post("/auth/supplier-magic-login", { token: magicToken });
    const { token, user: userData, refresh_token: refreshToken } = res.data;
    localStorage.setItem("scaffoindia_token", token);
    localStorage.setItem("scaffoindia_user", JSON.stringify(userData));
    if (refreshToken) localStorage.setItem("scaffoindia_refresh_token", refreshToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const res = await api.post("/auth/register", data);
    const { token, user: userData } = res.data;
    localStorage.setItem("scaffoindia_token", token);
    localStorage.setItem("scaffoindia_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const registerBuyer = async (data) => {
    const res = await api.post("/auth/buyer/register", data);
    const { token, user: userData } = res.data;
    localStorage.setItem("scaffoindia_token", token);
    localStorage.setItem("scaffoindia_user", JSON.stringify(userData));
    setUser(userData);
    if (userData?.role === "buyer") syncBuyerProfileToDraft(userData);
    return userData;
  };

  const updateBuyerProfile = async (data) => {
    const res = await api.put("/buyer/profile", data);
    const u = res.data;
    localStorage.setItem("scaffoindia_user", JSON.stringify(u));
    setUser(u);
    syncBuyerProfileToDraft(u);
    return u;
  };

  const logout = () => {
    // Revoke the server-side session while the access token is still available,
    // then drop the cached session locally.
    const token = localStorage.getItem("scaffoindia_token");
    const refreshToken = localStorage.getItem("scaffoindia_refresh_token");
    if (token) {
      // Pass the header explicitly: storage is cleared below before the request interceptor runs.
      api
        .post("/auth/logout", refreshToken ? { refresh_token: refreshToken } : {}, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => {});
    }
    clearAuthStorage();
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("scaffoindia_token");
    if (!token) return null;
    const res = await api.get("/auth/me");
    const u = res.data;
    localStorage.setItem("scaffoindia_user", JSON.stringify(u));
    setUser(u);
    if (u?.role === "buyer") syncBuyerProfileToDraft(u);
    return u;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, loginWithMagicToken, register, registerBuyer, updateBuyerProfile, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
